import { FC } from "react";
import { Box, Typography, Chip } from "@mui/material";

import { Claim } from "../../../../types/claims";
import { formatCurrency, formatDisplayDate } from "../../../helpers/dataHelper";

interface ClaimKeyInfoSectionProps {
  claim: Claim;
  onDenialCodeClick: (code: string) => void;
}

const ClaimKeyInfoSection: FC<ClaimKeyInfoSectionProps> = ({
  claim,
  onDenialCodeClick,
}) => (
  <Box sx={{ p: 2 }}>
    {/* Denial Reasons */}
    {claim.denialReason && (
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: "0.65rem",
            fontWeight: 600,
            letterSpacing: "0.03em",
            whiteSpace: "nowrap",
          }}
        >
          DENIAL REASONS
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {Array.from(
            new Set(
              claim.denialReason
                .split(/[,;]\s*/)
                .filter(Boolean)
                .map((c) => c.trim()),
            ),
          ).map((code, idx) => (
            <Chip
              key={idx}
              label={code}
              size="small"
              onClick={() => onDenialCodeClick(code)}
              sx={{
                height: 20,
                borderRadius: "5px",
                backgroundColor: "transparent",
                border: "1px solid #FCA5A5",
                cursor: "pointer",
                "&:hover": { backgroundColor: "#FEF2F2" },
                "& .MuiChip-label": {
                  color: "#DC2626",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  px: 1,
                  lineHeight: 2,
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          ))}
        </Box>
      </Box>
    )}

    {/* Row 1: Date of Service, Billed, Paid */}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
        gap: 1.5,
        mb: 1.5,
      }}
    >
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.65rem" }}
        >
          DATE OF SERVICE
        </Typography>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ fontSize: "0.8rem" }}
        >
          {formatDisplayDate(claim.dateOfService)}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.65rem" }}
        >
          BILLED
        </Typography>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ fontSize: "0.8rem" }}
        >
          {formatCurrency(claim.billedAmount)}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.65rem" }}
        >
          PAID
        </Typography>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            fontSize: "0.8rem",
            color: claim.paidAmount > 0 ? "success.main" : "text.secondary",
          }}
        >
          {formatCurrency(claim.paidAmount)}
        </Typography>
      </Box>
    </Box>

    {/* Row 2: Submitted, Payer, Provider */}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
        gap: 1.5,
      }}
    >
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.65rem" }}
        >
          SUBMITTED
        </Typography>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ fontSize: "0.8rem" }}
        >
          {formatDisplayDate(claim.dateSubmitted)}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.65rem" }}
        >
          PAYER
        </Typography>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            fontSize: "0.8rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {claim.payer || "-"}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.65rem" }}
        >
          PROVIDER
        </Typography>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            fontSize: "0.8rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {claim.provider || "-"}
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default ClaimKeyInfoSection;
